/**
 * Firebase Configuration
 * Used for OTP/Phone authentication
 */

import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { 
  getAuth, 
  Auth,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  PhoneAuthProvider,
  signInWithCredential,
  ConfirmationResult
} from 'firebase/auth';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';

// Firebase configuration - Replace with your Firebase project credentials
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || '',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
};

// reCAPTCHA Enterprise site key
const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY || '6LeNqUIsAAAAABiNEZnXRjquVkYamWP-WCn1OwBr';

// Check if Firebase is configured
export const isFirebaseConfigured = (): boolean => {
  return !!(firebaseConfig.apiKey && firebaseConfig.authDomain && firebaseConfig.projectId && firebaseConfig.appId);
};

// Validate Firebase configuration
const validateFirebaseConfig = () => {
  const requiredFields = ['apiKey', 'authDomain', 'projectId', 'appId'];
  const missing = requiredFields.filter(field => !firebaseConfig[field as keyof typeof firebaseConfig]);
  
  if (missing.length > 0) {
    console.warn('⚠️ Missing Firebase configuration:', missing);
    console.warn('Phone OTP will fall back to Supabase. To use Firebase, add these to .env:');
    missing.forEach(field => {
      console.warn(`  - VITE_FIREBASE_${field.toUpperCase().replace(/([A-Z])/g, '_$1')}`);
    });
    return false;
  }
  
  console.log('✅ Firebase configuration loaded successfully');
  return true;
};

// Initialize Firebase (only once)
let app: FirebaseApp | null = null;
let auth: Auth | null = null;

try {
  if (!getApps().length) {
    if (validateFirebaseConfig()) {
      app = initializeApp(firebaseConfig);
      
      // Initialize App Check with reCAPTCHA Enterprise
      try {
        initializeAppCheck(app, {
          provider: new ReCaptchaEnterpriseProvider(recaptchaSiteKey),
          isTokenAutoRefreshEnabled: true
        });
        console.log('✅ Firebase App Check initialized with reCAPTCHA Enterprise');
      } catch (appCheckError) {
        console.warn('⚠️ App Check initialization failed:', appCheckError);
      }
      
      auth = getAuth(app);
      auth.languageCode = 'en';
      
      // Production mode - reCAPTCHA verification required
      auth.settings.appVerificationDisabledForTesting = false;
      
      console.log('✅ Firebase Auth initialized successfully');
      console.log('📱 Phone authentication is ready (production mode)');
    }
  } else {
    app = getApps()[0];
    auth = getAuth(app);
  }
} catch (error) {
  console.error('❌ Firebase initialization error:', error);
  // Don't throw - allow fallback to Supabase
}

export { app, auth, RecaptchaVerifier, signInWithPhoneNumber, PhoneAuthProvider, signInWithCredential, recaptchaSiteKey };
export type { ConfirmationResult };
